import React, { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Person } from 'types';
import { useConfirmApi } from 'utils/api/ApiHooks';
import { getPrettyDate } from 'utils/util/util';
import SimpleErrorPage from 'views/Error/SimpleErrorPage';
import RichTextViewer from 'views/Widgets/Inputs/RichTextViewer';
import Loading from 'views/Widgets/Loading';
import FilterablePeopleTable from 'views/Widgets/People/FilterablePeopleTable';
import { FORMAT_AVATAR_WITH_TITLE } from 'views/Widgets/People/Filters/common';

export enum AuditEntryType {
  ADMIN_MANAGEMENT = 'A',
  FEEDBACK_MANAGEMENT = 'F',
  CUSTOM_SURVEYS_MANAGEMENT = 'C',
  AI_MANAGEMENT = 'I',
  ONE_ON_ONE_MANAGEMENT = 'O',
  DEVELOPMENT_PLAN_MANAGEMENT = 'D',
  PROMOTION_PACKET_MANAGEMENT = 'P',
  FEATURE_MANAGEMENT = 'E',
}

type AuditEntry = {
  author: Person;
  created_at: string;
  description: string;
};

interface Props {
  auditEntryTypes?: AuditEntryType[];
}

const OrganizationAudit: FC<Props> = ({ auditEntryTypes }) => {
  const { formatMessage, locale } = useIntl();

  const params = useMemo(() => {
    if (!auditEntryTypes || auditEntryTypes.length === 0) {
      return undefined;
    }
    return {
      type: auditEntryTypes.join(','),
    };
  }, [auditEntryTypes]);

  const { data, error, status } = useConfirmApi<{ results: AuditEntry[] }>({
    url: `/audit-entries`,
    params,
    method: 'GET',
  });

  const columns = useMemo(
    () => [
      {
        name: formatMessage({
          id: 'app.views.administration.people_administration.audit.organization_audit.table_header.date_and_time',
          defaultMessage: 'Date and time',
        }),
        field: 'created_at',
        format: (dateString: string) =>
          getPrettyDate({ dateString, includeTime: true, locale }),
        className: 'col-2',
      },
      {
        name: formatMessage({
          id: 'app.views.administration.people_administration.audit.organization_audit.table_header.author',
          defaultMessage: 'Author',
        }),
        field: 'author',
        isPerson: true,
        format: FORMAT_AVATAR_WITH_TITLE,
        className: 'col-2',
      },
      {
        name: formatMessage({
          id: 'app.views.administration.people_administration.audit.organization_audit.table_header.description',
          defaultMessage: 'Description',
        }),
        field: 'description',
        className: 'col-auto',
        sortable: false,
      },
    ],
    [formatMessage, locale]
  );

  const rows = useMemo(() => {
    if (!data) {
      return [];
    }

    return data.results.map((entry) => ({
      ...entry,
      description: <RichTextViewer model={entry.description} expanded={true} />,
    }));
  }, [data]);

  if (status === 'LOADING') {
    return (
      <Loading
        message={formatMessage({
          id: 'app.views.administration.people_administration.audit.organization_audit.message.loading_audit_entries',
          defaultMessage: 'Loading audit entries...',
        })}
      />
    );
  }

  if (!data || error) {
    return <SimpleErrorPage error={error} />;
  }

  return (
    <FilterablePeopleTable
      rows={rows}
      columns={columns}
      hideFilters={true}
      hideExportButton={true}
    />
  );
};

export default React.memo(OrganizationAudit);
